"use client";

import Image from "next/image";
import {
  Tag,
  Button,
  Card,
  Icon,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingSpinner,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@focal/ui-kit";
import { ReactNode, useState } from "react";
import OverflowTooltip from "../OverflowTooltip/OverflowTooltip";
import { InstalledIntegrationStatus } from "@/types/installedintegration";
import { cn } from "@/lib/utils";

export const DEFAULT_MAX_ACCOUNTS = 5;

export interface PillAccount {
  /** InstalledIntegration id — used for disconnect */
  id: string;
  email: string | null;
  /** Credential health of the underlying integration row. When
   *  NEEDS_RECONNECT, the pill renders in a warning variant with an
   *  explanatory tooltip. Defaults to ACTIVE when omitted. */
  status?: InstalledIntegrationStatus;
}

export interface PillListDisconnectCopy {
  title?: string;
  description?: (email: string) => ReactNode;
}

export interface PillListProps {
  label: string;
  iconSrc: string;
  description?: string;
  accounts: PillAccount[];
  /** Hide the connect pill once this many accounts are connected. Defaults to {@link DEFAULT_MAX_ACCOUNTS}. */
  maxAccounts?: number;
  onConnect: () => void;
  onDisconnect: (id: string) => void;
  isDisabled?: boolean;
  /** Show spinner + "Connecting..." on the connect pill. */
  isBusy?: boolean;
  /** Account ids being disconnected — the matching pill shows a spinner and
   *  disables its remove button until the id clears. */
  disconnectingIds?: Set<string>;
  /** Tooltip wrapping the connect pill (e.g., "must be configured by your admin"). */
  connectTooltip?: string;
  /** When true, the connect pill is replaced by a retry button until cleared. */
  hasError?: boolean;
  onRetry?: () => void;
  /** When provided, renders a "Configure" button (e.g. for firm admins) that
   *  opens the firm-level integration settings. Mirrors the CRM integrations. */
  onConfigure?: () => void;
  /** Prefix for data-cy attributes: `${dataCyPrefix}-option`, `${dataCyPrefix}-add`, etc. */
  dataCyPrefix: string;
  /** Override the disconnect-confirmation dialog copy. */
  disconnectCopy?: PillListDisconnectCopy;
}

function DisconnectConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  email,
  copy,
  label,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  email: string;
  copy?: PillListDisconnectCopy;
  label: string;
}) {
  const title = copy?.title ?? `Remove ${label}`;
  const description = copy?.description ? (
    copy.description(email)
  ) : (
    <>
      Are you sure you want to disconnect <strong>{email}</strong>?
    </>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PillList({
  label,
  iconSrc,
  description,
  accounts,
  maxAccounts = DEFAULT_MAX_ACCOUNTS,
  onConnect,
  onDisconnect,
  isDisabled = false,
  isBusy = false,
  disconnectingIds,
  connectTooltip,
  hasError = false,
  onRetry,
  onConfigure,
  dataCyPrefix,
  disconnectCopy,
}: PillListProps) {
  const [confirmDisconnectId, setConfirmDisconnectId] = useState<string | null>(
    null,
  );
  const disconnectAccount = accounts.find((a) => a.id === confirmDisconnectId);
  const isConnected = accounts.length > 0;
  const showConnectPill = accounts.length < maxAccounts;
  const showRetry = hasError && !!onRetry && !isConnected;

  const connectPill = (
    <button
      className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm border border-dashed border-border text-typography hover:text-typography-primary hover:border-primary/30 transition-colors hover:cursor-pointer self-center size-fit disabled:pointer-events-none disabled:bg-background-disabled disabled:text-typography-disabled disabled:border-transparent"
      onClick={onConnect}
      disabled={isDisabled || isBusy}
      aria-busy={isBusy}
      data-cy={`${dataCyPrefix}-add`}
    >
      {isBusy ? (
        <>
          <LoadingSpinner className="mr-1 h-3 w-3" />
          Connecting...
        </>
      ) : (
        <>
          <Icon name="AddIcon" size="xs" />
          {isConnected ? "Add" : "Connect"}
        </>
      )}
    </button>
  );

  return (
    <>
      <Card
        className="p-4 flex flex-col gap-3 min-h-40"
        data-cy={`${dataCyPrefix}-option`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image src={iconSrc} alt={label} width={20} height={20} />
            <span className="font-medium text-typography-primary">{label}</span>
            {isConnected && (
              <Tag variant="green" data-cy={`${dataCyPrefix}-enabled`}>
                Enabled
              </Tag>
            )}
          </div>
        </div>
        {!!description && (
          <p className="text-typography-secondary text-sm">{description}</p>
        )}
        <div
          className="flex flex-col items-start justify-end gap-2 flex-1"
          data-cy={`${dataCyPrefix}-pills`}
        >
          {accounts.map((account) => {
            const needsReconnect =
              account.status === InstalledIntegrationStatus.NEEDS_RECONNECT;
            const isDisconnecting = !!disconnectingIds?.has(account.id);
            const labelText = account.email || "Loading...";
            const labelEl = needsReconnect ? (
              <span className="flex items-center gap-1 min-w-0">
                <Icon
                  name="WarningIcon"
                  size="xs"
                  className="fill-warning shrink-0"
                />
                <span className="truncate min-w-0">{labelText}</span>
              </span>
            ) : (
              <OverflowTooltip
                delayDuration={400}
                side="top"
                content={
                  <p className="whitespace-pre-wrap text-center">{labelText}</p>
                }
              >
                {labelText}
              </OverflowTooltip>
            );
            const pillClass = cn(
              "inline-flex items-center overflow-hidden gap-1.5 rounded-full border pl-3 pr-1.5 py-1 text-sm size-fit max-w-full",
              needsReconnect
                ? "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20 transition-colors hover:cursor-pointer disabled:pointer-events-none disabled:opacity-60"
                : "border-border bg-muted/50 text-typography-primary",
              isDisconnecting && "opacity-60",
            );
            const disconnectButton = (
              <button
                className={cn(
                  "shrink-0 rounded-full p-0.5 transition-colors hover:cursor-pointer disabled:pointer-events-none disabled:text-typography-disabled",
                  needsReconnect
                    ? "text-warning hover:bg-warning/30"
                    : "text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDisconnectId(account.id);
                }}
                disabled={isDisabled || isDisconnecting}
                aria-busy={isDisconnecting}
                aria-label={
                  isDisconnecting
                    ? `Disconnecting ${account.email ?? "account"}`
                    : `Disconnect ${account.email ?? "account"}`
                }
                data-cy={`${dataCyPrefix}-disconnect`}
              >
                {isDisconnecting ? (
                  <span data-cy={`${dataCyPrefix}-disconnect-spinner`}>
                    <LoadingSpinner className="h-3 w-3" />
                  </span>
                ) : (
                  <Icon name="CloseIcon" size="xs" />
                )}
              </button>
            );
            const pillContent = (
              <>
                {labelEl}
                {disconnectButton}
              </>
            );
            if (needsReconnect) {
              return (
                <Tooltip key={account.id} delayDuration={200}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      data-cy={`${dataCyPrefix}-pill`}
                      data-status="NEEDS_RECONNECT"
                      className={pillClass}
                      onClick={onConnect}
                      disabled={isDisabled || isBusy || isDisconnecting}
                      aria-busy={isBusy || isDisconnecting}
                      aria-label={`Reconnect ${account.email ?? "account"}`}
                    >
                      {pillContent}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="whitespace-pre-wrap max-w-55 text-center">
                      This connection needs to be reconnected. Click to refresh
                      access.
                    </p>
                  </TooltipContent>
                </Tooltip>
              );
            }
            return (
              <span
                key={account.id}
                data-cy={`${dataCyPrefix}-pill`}
                data-status={account.status ?? "ACTIVE"}
                className={pillClass}
              >
                {pillContent}
              </span>
            );
          })}

          {(showRetry || showConnectPill || onConfigure) && (
            <div className="flex items-center gap-2">
              {showRetry ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  data-cy={`${dataCyPrefix}-retry`}
                >
                  Retry connection
                </Button>
              ) : showConnectPill ? (
                connectTooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{connectPill}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="whitespace-pre-wrap max-w-55 text-center">
                        {connectTooltip}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  connectPill
                )
              ) : null}

              {onConfigure && (
                <Button
                  variant="ghost"
                  onClick={onConfigure}
                  data-cy={`${dataCyPrefix}-configure`}
                >
                  Configure
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <DisconnectConfirmDialog
        open={!!confirmDisconnectId}
        onOpenChange={(open) => !open && setConfirmDisconnectId(null)}
        onConfirm={() => {
          if (confirmDisconnectId) onDisconnect(confirmDisconnectId);
          setConfirmDisconnectId(null);
        }}
        email={disconnectAccount?.email || "this account"}
        copy={disconnectCopy}
        label={label}
      />
    </>
  );
}
